import { useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { RateImpressionProvider } from 'contexts/RateImpressionContext'
import ContactContext from 'contexts/contactContext'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import uuidV4 from 'lib/string/uuidV4Utils'
import {
  getCurrentAvailableRates,
  getPropertyCombinedRatesByRoom,
  getPropertyLowestAvailableStayPayTier,
  getPropertyRatesKey,
  arePropertyRatesLoading,
} from 'selectors/offerPage/offerPageSelectors'

import PropertyRoomOption from './PropertyRoomOption'
import type { RoomRateViews } from './singlePropertyUtils'
import usePropertyMetasearchPriceAccuracyTracking from 'hooks/OfferPage/usePropertyMetasearchPriceAccuracyTracking'
import Sheet from 'components/utils/Sheet'
import { verticalChildSpacing } from 'components/utils/spacing'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import styled from 'styled-components'
import { rem } from 'polished'

const OptionsContainer = styled(Sheet)`
  ${verticalChildSpacing[8]}

  ${mediaQueryUp.desktop} {
    ${verticalChildSpacing[32]}
  }

  ${mediaQueryDown.mobile} {
    padding-block: ${rem(8)};
  }

  &.flush-top {
    margin-top: 0;
    padding-top: 0;
  }
`

interface Props {
  property: App.Property.Property
  /**
   * @deprecated Use `property` instead. (SPP combines multiple offers into a single property; there
   * is no canonical offer.)
   */
  offer: App.HotelOffer | undefined
  combinedRatesByRoom: Array<RoomRateViews>
  hasRateError: boolean
  checkIn?: string
  availabilityKey: string | undefined
  ratesLoading: boolean
  onBookNowInvalid?: () => void
  onViewPackageOptions: () => void
  lowestAvailableStayPayTier?: App.StayPayTier | null
  /** Removes the top spacing of the room list so it sits flush against a preceding element. */
  flushTop?: boolean
}

function PropertyRoomOptions(props: Props) {
  const {
    property,
    offer,
    combinedRatesByRoom,
    hasRateError,
    onViewPackageOptions,
    checkIn,
    availabilityKey,
    ratesLoading,
    lowestAvailableStayPayTier,
    flushTop,
  } = props

  /************************************************************************************************/
  /* STOP! Do not add any more hooks for deriving packages/rooms or options/rates to this         */
  /* component! This logic is being refactored out into selectors! If you _need_ to add new       */
  /* logic, speak to Xiao.                                                                        */
  /************************************************************************************************/

  const intl = useIntl()
  const supportContact = useContext(ContactContext)

  // GHA price-accuracy tracking
  usePropertyMetasearchPriceAccuracyTracking()

  const highlightedPackage = useMemo(
    () => {
      const selectedRoom = combinedRatesByRoom.find(({ isSelected }) => isSelected)
      const highlightedRoom = combinedRatesByRoom.find(({ isHighlighted }) => isHighlighted)
      // If we've selected a different room from the GHA-highlighted room, then suppress GHA
      // highlighting.
      if (!selectedRoom || selectedRoom === highlightedRoom) return highlightedRoom
    },
    [combinedRatesByRoom],
  )

  const roomListId = useMemo(() => uuidV4(),
  // oxlint-disable-next-line react-hooks/exhaustive-deps
    [combinedRatesByRoom]) // regenerate id if list of rooms changes

  // Because we might promote a room with a highlighted rate to the top, we can't use list item
  // indices as the basis for our counter.
  let nextRoomPosition = 1
  return (<RateImpressionProvider availabilityKey={availabilityKey} ratesLoading={ratesLoading}>
    <VerticalSpacer gap={16}>
      <LayoutContainer>
        {hasRateError && <MessageBanner
          kind="warning"
          tabletAlign="center"
          heading={intl.formatMessage(
            {
              id: 'BnvLw0OFjcVH9KIHccT2',
              defaultMessage: 'The third party booking system for {propertyName} is currently down',
              description: 'Error message heading when property booking system is down',
            },
            { propertyName: property.name },
          )}
        >
          {intl.formatMessage(
            {
              id: 'tGbUfKIAF_biyC8JvkSw',
              defaultMessage: 'Please call our VIP team on {phoneNumber} who are ready to provide assistance to complete your booking',
              description: 'Error message body with phone number when property booking system is down',
            },
            { phoneNumber: supportContact.local.humanReadable },
          )}
        </MessageBanner>}
        {!checkIn && lowestAvailableStayPayTier && <MessageBanner
          kind="promotional"
          icon={<LineInfoCircleIcon />}
        >
          <FormattedMessage
            id="w_UoFwnNZR6UH1nCXBD5"
            defaultMessage="<b>Special offer:</b> Enjoy free nights for stays longer than {dayCountLabel}. Select dates to check eligibility."
            description="Stay pay tier promotional banner message in the loyalty section"
            values={{
              dayCountLabel: intl.formatMessage(
                {
                  id: '--kBF6UkfAMNYHooUklh',
                  defaultMessage: '{count, plural, one {# day} other {# days}}',
                  description: 'Stay length day count label shown in special offer banner',
                },
                { count: lowestAvailableStayPayTier.stayNights },
              ),
              b: (chunks) => <b>{chunks}</b>,
            }}
          />
        </MessageBanner>}
      </LayoutContainer>
      {!hasRateError && <OptionsContainer background="light-background" tabletBackground="none" className={flushTop ? 'flush-top' : undefined}>
        {highlightedPackage && !!highlightedPackage.rates.length && <PropertyRoomOption
          property={property}
          offer={offer}
          roomId={highlightedPackage.roomId}
          propertyRoom={highlightedPackage.propertyRoom}
          availableRooms={highlightedPackage.availableRooms}
          rates={highlightedPackage.rates}
          allRatesQuickFiltered={highlightedPackage.allRatesQuickFiltered}
          roomFitsCapacity={highlightedPackage.roomFitsCapacity}
          datesAvailable={highlightedPackage.datesAvailable}
          isSelected={highlightedPackage.isSelected}
          isHighlighted
          roomRateUnmatched={highlightedPackage.roomRateUnmatched}
          onViewPackageOptions={onViewPackageOptions}
          roomListId={roomListId}
          roomPosition={nextRoomPosition++}
          roomTypeCount={combinedRatesByRoom.length}
        />}
        {combinedRatesByRoom.map((item) => {
          if (highlightedPackage && highlightedPackage.roomId === item.roomId) {
            return null
          }
          return <PropertyRoomOption
            key={item.roomId}
            property={property}
            offer={offer}
            roomId={item.roomId}
            propertyRoom={item.propertyRoom}
            availableRooms={item.availableRooms}
            rates={item.rates}
            allRatesQuickFiltered={item.allRatesQuickFiltered}
            roomFitsCapacity={item.roomFitsCapacity}
            datesAvailable={item.datesAvailable}
            isSelected={item.isSelected}
            onViewPackageOptions={onViewPackageOptions}
            roomListId={roomListId}
            roomPosition={nextRoomPosition++}
            roomTypeCount={combinedRatesByRoom.length}
          />
        })}
      </OptionsContainer>}
    </VerticalSpacer>
  </RateImpressionProvider>
  )
}

type MappedProps = Pick<Props,
  'property' |
  'offer' |
  'combinedRatesByRoom' |
  'hasRateError' |
  'checkIn' |
  'availabilityKey' |
  'ratesLoading' |
  'lowestAvailableStayPayTier'
> & { open?: boolean }

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  property: state.property!,
  offer: state.offer as App.HotelOffer | undefined,
  combinedRatesByRoom: getPropertyCombinedRatesByRoom(state, appState),
  hasRateError: !!getCurrentAvailableRates(state, appState)?.error,
  checkIn: state.checkInDate,
  availabilityKey: getPropertyRatesKey(state, appState),
  ratesLoading: arePropertyRatesLoading(state, appState),
  lowestAvailableStayPayTier: getPropertyLowestAvailableStayPayTier(state, appState),
  open: !!state.activeFilter,
}), PropertyRoomOptions, preventRenderWithOpenFilter)
